/**
 * Service for email integration API calls.
 */
import { AxiosError, AxiosRequestConfig } from 'axios';
import api from './api';

/**
 * Extract error message from API response.
 * FastAPI returns errors in { detail: "message" } format.
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data;
    if (typeof data === 'object' && 'detail' in data) {
      return String(data.detail);
    }
    if (typeof data === 'string') {
      return data;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

// Request types

interface DiscoverProviderRequest {
  email_address: string;
}

interface ConnectEmailRequest {
  email_address: string;
  password?: string;
  display_name?: string;
  /** #1852: manual IMAP host for a custom-domain (provider === 'unknown'). */
  imap_host?: string;
  /** #1852: fixed 993 for manual hosts (implicit TLS enforced server-side). */
  imap_port?: number;
  /**
   * #3035: the IMAP folders the farmer chose to poll. Omitted (never `[]`) when the tree
   * could not be enumerated — the server then keeps its own INBOX+Sent default, and an
   * explicit empty list is refused with a 400 by design.
   */
  folders?: string[];
  /**
   * #3253: how much mailbox history to backfill on connect. One of the five spellings
   * the server accepts — `none` | `last_10` | `last_100` | `1year` | `3years` — sent
   * verbatim; the retired `1_year`/`3_years` spellings are rejected with a 400.
   *
   * Omitted (never `null`) when the farmer did not choose: the server's field is
   * non-optional with a `last_10` default, so an explicit `null` fails validation.
   */
  historical_import?: HistoricalImportSetting;
}

/**
 * #3253: the five values `HISTORICAL_IMPORT_SETTINGS` accepts, server-side.
 *
 * Deliberately declared here rather than imported from the design system: this is the
 * WIRE vocabulary, and the service layer should not depend on a UI package to describe
 * it. Drift between the two is still caught automatically — `IntegrationsPage` assigns
 * the modal's `ConnectEmailFormData['historicalImport']` straight into this field, so a
 * value the DS starts offering and the wire does not accept fails `tsc` at that seam.
 */
export type HistoricalImportSetting = 'none' | 'last_10' | 'last_100' | '1year' | '3years';

interface TestConnectionRequest {
  email_address: string;
  password: string;
  /** #1852: manual IMAP host for a custom-domain (provider === 'unknown'). */
  imap_host?: string;
  /** #1852: fixed 993 for manual hosts (implicit TLS enforced server-side). */
  imap_port?: number;
}

// Response types

interface DiscoverProviderResponse {
  provider: string;
  provider_name: string;
  auth_method: 'password' | 'oauth';
  requires_oauth: boolean;
  oauth_url: string | null;
  imap_host: string | null;
  imap_port: number | null;
  /**
   * #3032: whether the farmer may override the derived `imap_host`. True for every
   * password provider — a self-hosted mailbox on a domain whose MX points at a
   * hoster has no other way to escape that hoster's shared IMAP server.
   *
   * Nullable/optional on purpose: a pre-#3032 integration-service (reachable during a
   * rolling deploy) sends no decision at all, and the BFF preserves that as `null`
   * rather than inventing `false`. Absent/null must therefore stay distinguishable
   * from an explicit `false`, so the modal can fall back to its old
   * `provider === 'unknown'` rule instead of hiding the host field outright.
   */
  allow_manual_override?: boolean | null;
}

type SyncScheduleMode = 'automatic' | 'daily' | 'weekly';

interface EmailIntegration {
  id: string;
  email_address: string;
  display_name: string | null;
  provider: string;
  provider_name: string;
  /**
   * #3517: `paused` is a real server state — the poller selects only active-or-retryable-
   * error rows, so a paused mailbox has always stopped polling. It was missing from this
   * union, so `mapConnectionStatus`'s `default:` arm rendered it as **error**: during the
   * 2026-08-17/18 WF incident an operator parked the mailbox to stop a runaway import and
   * the farmer's UI told them their integration was broken, which is plausibly what
   * prompted the next disconnect/reconnect — the cycle that re-armed the import.
   */
  connection_status: 'pending' | 'connecting' | 'active' | 'error' | 'disconnected' | 'paused';
  last_checked_at: string | null;
  last_successful_at: string | null;
  error_message: string | null;
  created_at: string;
  last_poll_emails_fetched?: number | null;
  sync_schedule: SyncScheduleMode;
  sync_schedule_time: string | null;
  sync_schedule_day: number | null;
  /** #1570: store the email body as a separate PDF during ingest (default off). */
  extract_email_body_pdf: boolean;
  /**
   * #3035: the IMAP folders this mailbox polls. Optional because a pre-#3035
   * integration-service omits it during a rolling deploy; `undefined` must stay
   * distinguishable from `[]`, since `[]` means "polls nothing" and gets a warning.
   */
  polling_folders?: string[];
  /**
   * #3184: wire name → RFC 3501 mUTF-7-decoded display name for `polling_folders`
   * (`INBOX.Grundh&APY-fer 2022` → `INBOX.Grundhöfer 2022`); only entries whose decode
   * differs are present. Labels only — everything submitted stays the wire name. Optional
   * because a pre-#3184 backend omits it (rolling deploy); absent = render raw.
   */
  polling_folder_display_names?: Record<string, string>;
  /**
   * #3035/#2674/#3545: non-fatal degradation codes — `no_polling_folders`,
   * `sent_folder_undiscoverable`, `folders_unreadable`. Deliberately not part of
   * `connection_status`: they explain a gap in a working mailbox rather than taking it
   * down. Additive by design — the server may add a code without a contract change, and
   * the card ignores any it does not render.
   */
  status_warnings?: string[];
  /**
   * #3431: outcome of the most recent "Jetzt prüfen". `check-now` is 202 + background
   * (a manual poll on a large mailbox legitimately takes minutes), so this is where its
   * result lands — the integration refetch the UI already does after a check IS the poll
   * surface. Optional: a pre-#3431 integration-service omits it during a rolling deploy,
   * and `undefined` means "never checked", not "idle".
   */
  check_status?: ManualCheckStatus | null;
}

/** #3431: state of a backgrounded check-now. */
interface ManualCheckStatus {
  /**
   * `skipped_locked` is deliberately distinct from `completed`: it means the scheduled
   * poller held the lock and this check did nothing at all. Collapsing the two is the
   * bug that let a manual click report "Found 0 emails" about a poll that never ran.
   */
  status: 'running' | 'completed' | 'failed' | 'skipped_locked';
  started_at: string | null;
  finished_at: string | null;
  emails_fetched: number;
  emails_submitted: number;
  message: string | null;
}

interface UpdateScheduleRequest {
  sync_schedule: SyncScheduleMode;
  sync_schedule_time?: string | null;
  sync_schedule_day?: number | null;
}

interface UpdateSettingsRequest {
  /** #1570: store the email body as a separate PDF during ingest. */
  extract_email_body_pdf?: boolean;
  /** #3035: the polled-folder selection. An empty array is refused with a 400. */
  folders?: string[];
}

/**
 * #3035: an already-connected mailbox's folder tree.
 *
 * `outcome` is a tri-state and the distinction drives three different screens (#2674's
 * lesson): `resolved` → render the picker; `empty` → the mailbox has nothing pollable;
 * `error` → we learned nothing, so the current `selected` set is unchanged.
 */
interface FolderListResponse {
  outcome: 'resolved' | 'empty' | 'error';
  folders: string[];
  selected: string[];
  error_message: string | null;
  /**
   * #3184: wire → decoded labels over the union of `folders` and `selected` (a persisted
   * encoded name the server no longer offers still needs a label). Optional: absent
   * (pre-#3184 backend) and `{}` both mean "render the wire names".
   */
  display_names?: Record<string, string>;
}

interface ListIntegrationsResponse {
  integrations: EmailIntegration[];
  total: number;
}

interface TestConnectionResponse {
  success: boolean;
  error_message: string | null;
  /**
   * #3032: the class of failure — `auth_rejected` / `host_unreachable` /
   * `tls_failed` / `timeout` / `server_aborted` / `unknown`. `error_message` is
   * already the German rendering of it (a host problem no longer says "password
   * wrong"); this is the machine-readable form for logging and future branching.
   */
  failure_reason?: string | null;
  /**
   * #3035: which of the three enumeration outcomes the server saw — `resolved` (picker),
   * `empty` (nothing pollable; block), `error` (nothing learned; the server default
   * applies). Optional/absent means a pre-#3035 backend, treated as `error`.
   */
  folder_enumeration?: 'resolved' | 'empty' | 'error' | null;
  /** #3035: every folder the server offers as a poll target. */
  folders?: string[];
  /** #3035: the server's suggestion — INBOX + its real Sent folder (today's behaviour). */
  suggested_folders?: string[];
  /** #3184: wire → decoded labels for `folders` (labels only; submit wire names). */
  display_names?: Record<string, string>;
}

interface CheckNowResponse {
  status: string;
  message: string;
  emails_fetched: number;
  emails_submitted: number;
  last_checked_at: string | null;
}

interface DisconnectResponse {
  status: string;
  message: string;
}

export const integrationsService = {
  /**
   * Discover email provider from email address.
   * Uses MX lookup to detect provider and auth method.
   */
  discoverProvider: async (emailAddress: string): Promise<DiscoverProviderResponse> => {
    try {
      const request: DiscoverProviderRequest = { email_address: emailAddress };
      const response = await api.post<DiscoverProviderResponse>('/email/discover', request);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to discover email provider'));
    }
  },

  /**
   * Test email credentials without storing.
   *
   * #1852: for a custom-domain provider (provider === 'unknown') the caller
   * supplies a manual IMAP host; the port is fixed at 993 server-side.
   */
  testConnection: async (
    emailAddress: string,
    password: string,
    imapHost?: string,
    imapPort?: number
  ): Promise<TestConnectionResponse> => {
    try {
      const request: TestConnectionRequest = {
        email_address: emailAddress,
        password,
        ...(imapHost ? { imap_host: imapHost, imap_port: imapPort ?? 993 } : {}),
      };
      const response = await api.post<TestConnectionResponse>('/email/test', request);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Connection test failed'));
    }
  },

  /**
   * Connect email integration.
   */
  // NOTE: deliberately NOT wrapped in `new Error(extractErrorMessage(...))` like its
  // siblings — `IntegrationsPage.handleConnect` reads `err.response.status` to show the
  // rate-limit toast, and wrapping would discard the response. The page extracts the
  // upstream `detail` itself instead (#3035).
  connectEmail: async (config: ConnectEmailRequest): Promise<EmailIntegration> => {
    const response = await api.post<EmailIntegration>('/email/connect', config);
    return response.data;
  },

  /**
   * List user's email integrations.
   */
  listIntegrations: async (config?: AxiosRequestConfig): Promise<ListIntegrationsResponse> => {
    // #1220: the list view polls on a 30s interval (see useIntegrations); the
    // hook passes `{ background: true }` so the api interceptor never tags a
    // poll interactive even when it fires inside the post-gesture window.
    const response = await api.get<ListIntegrationsResponse>('/email/integrations', config);
    return response.data;
  },

  /**
   * Get single integration by ID.
   */
  getIntegration: async (integrationId: string): Promise<EmailIntegration> => {
    const response = await api.get<EmailIntegration>(`/email/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * Trigger immediate email check.
   */
  checkNow: async (integrationId: string): Promise<CheckNowResponse> => {
    const response = await api.post<CheckNowResponse>(`/email/integrations/${integrationId}/check-now`);
    return response.data;
  },

  /**
   * Update sync schedule settings.
   */
  updateSchedule: async (integrationId: string, schedule: UpdateScheduleRequest): Promise<EmailIntegration> => {
    const response = await api.patch<EmailIntegration>(`/email/integrations/${integrationId}/schedule`, schedule);
    return response.data;
  },

  /**
   * Update non-schedule settings (e.g. extract email body as a separate PDF).
   * #1570.
   */
  // NOTE: like connectEmail, deliberately unwrapped — the callers' onError handlers read
  // `err.response.status` for the rate-limit toast. Callers extract the upstream `detail`
  // themselves (#3035).
  updateSettings: async (integrationId: string, settings: UpdateSettingsRequest): Promise<EmailIntegration> => {
    const response = await api.patch<EmailIntegration>(`/email/integrations/${integrationId}/settings`, settings);
    return response.data;
  },

  /**
   * #3035: list an already-connected mailbox's folder tree, so the polled-folder selection
   * is editable without asking the farmer to re-enter a password (the server uses the
   * stored Vault credentials).
   *
   * Costs a connect + LOGIN + LIST against the customer's mail server, so call it on
   * demand — when the editor opens — never on render.
   */
  listFolders: async (integrationId: string): Promise<FolderListResponse> => {
    try {
      const response = await api.get<FolderListResponse>(`/email/integrations/${integrationId}/folders`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Die Ordnerliste konnte nicht abgerufen werden.'));
    }
  },

  /**
   * Disconnect email integration.
   * Permanently deletes credentials and integration record.
   */
  disconnectIntegration: async (integrationId: string): Promise<DisconnectResponse> => {
    const response = await api.delete<DisconnectResponse>(`/email/integrations/${integrationId}`);
    return response.data;
  },
};

export type {
  DiscoverProviderResponse,
  EmailIntegration,
  FolderListResponse,
  ListIntegrationsResponse,
  ConnectEmailRequest,
  TestConnectionResponse,
  CheckNowResponse,
  SyncScheduleMode,
  UpdateScheduleRequest,
  UpdateSettingsRequest,
};
